import { isPlatformServer } from '@angular/common';
import { InjectionToken, Injectable, PLATFORM_ID, inject } from '@angular/core';

export interface RuntimeBasicAuth {
  user: string;
  access: string;
}

/**
 * Basic auth credentials injected at RUNTIME — never part of the build.
 * On the SSR server this is provided from process.env in app.config.server.ts.
 * In the browser it stays null and credentials are fetched from the
 * `runtime-config.json` endpoint instead.
 */
export const RUNTIME_BASIC_AUTH = new InjectionToken<RuntimeBasicAuth | null>('RUNTIME_BASIC_AUTH', {
  providedIn: 'root',
  factory: () => null
});

interface RuntimeConfigResponse {
  basic: RuntimeBasicAuth | null;
}

@Injectable({
  providedIn: 'root'
})
export class RuntimeConfigService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly serverBasicAuth = inject(RUNTIME_BASIC_AUTH);

  private basicAuthValue: RuntimeBasicAuth | null = null;

  get basicAuth(): RuntimeBasicAuth | null {
    return this.basicAuthValue;
  }

  /**
   * Loads runtime config once at bootstrap (provideAppInitializer).
   * Server: credentials come straight from the RUNTIME_BASIC_AUTH token.
   * Browser: fetched from the SSR `runtime-config.json` endpoint.
   * Any failure resolves without credentials (fail-safe: no header sent).
   */
  async load(): Promise<void> {
    if (isPlatformServer(this.platformId)) {
      this.basicAuthValue = this.serverBasicAuth;
      return;
    }

    try {
      const response = await fetch(new URL('runtime-config.json', document.baseURI), {
        cache: 'no-store'
      });
      if (!response.ok) {
        return;
      }
      const config = (await response.json()) as Partial<RuntimeConfigResponse>;
      this.basicAuthValue = config?.basic ?? null;
    } catch {
      // Fail-safe: run without basic auth rather than break the app.
      this.basicAuthValue = null;
    }
  }
}
